import React, { useState, useEffect, useRef, useMemo } from 'react';
import L from 'leaflet';
import {
  Package, Truck, Send, Plus, Edit2, Trash2, CheckCircle2, XCircle,
  RefreshCw, Check, X, Layers, Calendar, Clock, MapPin, ChevronRight, ChevronLeft,
  ChevronDown, ChevronUp, ArrowLeft, DollarSign, Shield, Info, Download,
  Filter, Search, Copy, Lock, Eye, AlertCircle, FileText, Share2, Sparkles,
  CheckSquare, ArrowRight, User, AlertTriangle, ExternalLink, Trash, Calculator
} from 'lucide-react';
import OrderCostSimulationModal from '../components/OrderCostSimulationModal';
import { createTileLayer } from '../utils/mapTiles';

// Format date helper: "Lundi 12 septembre 2026, 07:00 - 12:00"
function formatPlannedSchedule(startIso, endIso, fallbackStr = 'Vendredi 21 août 2026, 07:30 - 17:00') {
  if (!startIso) return fallbackStr;
  try {
    const s = new Date(startIso);
    const dayStr = s.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const capitalizedDay = dayStr.charAt(0).toUpperCase() + dayStr.slice(1);
    const startTime = s.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
    let endTime = '17:00';
    if (endIso) {
      const e = new Date(endIso);
      endTime = e.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
    }
    return `${capitalizedDay}, ${startTime} - ${endTime}`;
  } catch (e) {
    return fallbackStr;
  }
}

// Short format date helper: "ven. 21 août · 07:30"
function formatShortDate(startIso, fallbackStr = 'ven. 21 août · 07:30') {
  if (!startIso) return fallbackStr;
  try {
    const s = new Date(startIso);
    const dayName = s.toLocaleDateString('fr-CH', { weekday: 'short' });
    const dayNum = s.getDate();
    const monthName = s.toLocaleDateString('fr-CH', { month: 'short' });
    const time = s.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
    return `${dayName} ${dayNum} ${monthName} · ${time}`;
  } catch (e) {
    return fallbackStr;
  }
}

// Leaflet Map Component for Connect Postal Zones
function ConnectPostalMap({ selectedNpas = [], allRoundsNpas = [], onZipClick = null, height = '320px' }) {
  const mapRef = useRef(null);
  const leafletInstance = useRef(null);
  const geoJsonLayerRef = useRef(null);
  const [geoData, setGeoData] = useState(null);

  useEffect(() => {
    fetch('/swiss-zip-codes.geojson')
      .then(res => res.json())
      .then(data => setGeoData(data))
      .catch(err => console.warn('Could not load swiss-zip-codes.geojson:', err));
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!leafletInstance.current) {
      const map = L.map(mapRef.current, {
        center: [46.65, 7.05],
        zoom: 9,
        zoomControl: true,
        attributionControl: false
      });

      createTileLayer('pale').addTo(map);

      leafletInstance.current = map;
    }

    const map = leafletInstance.current;
    if (geoJsonLayerRef.current) {
      map.removeLayer(geoJsonLayerRef.current);
      geoJsonLayerRef.current = null;
    }

    if (geoData && map) {
      const selectedSet = new Set(selectedNpas.map(n => String(n).trim()));
      const allRoundsSet = new Set(allRoundsNpas.map(n => String(n).trim()));

      const layer = L.geoJSON(geoData, {
        style: (feature) => {
          const code = String(feature.properties?.plz || feature.properties?.postleitzahl || feature.properties?.zip || '');
          const isSelected = selectedSet.has(code);
          const isDeliveredAny = allRoundsSet.has(code);

          if (isSelected) {
            return {
              fillColor: '#38bdf8',
              weight: 2,
              opacity: 1,
              color: '#0284c7',
              fillOpacity: 0.65
            };
          }

          if (isDeliveredAny) {
            return {
              fillColor: '#ec4899',
              weight: 1.2,
              opacity: 0.9,
              color: '#be185d',
              fillOpacity: 0.38
            };
          }

          return {
            fillColor: '#1e293b',
            weight: 0.5,
            opacity: 0.4,
            color: '#334155',
            fillOpacity: 0.12
          };
        },
        onEachFeature: (feature, l) => {
          const code = String(feature.properties?.plz || feature.properties?.zip || '');
          const name = feature.properties?.langtext || feature.properties?.ortbez18 || feature.properties?.name || '';
          const isDelivered = allRoundsSet.has(code) || selectedSet.has(code);

          l.bindTooltip(`<b>${code}</b> ${name} ${isDelivered ? '<br/><span style="color:#38bdf8;font-size:10px;">✓ Desservi par une tournée</span>' : ''}`, {
            direction: 'top',
            className: 'connect-map-tooltip'
          });

          l.on('mouseover', () => {
            l.setStyle({ weight: 2.5, color: '#f43f5e' });
          });

          l.on('mouseout', () => {
            const isSelected = selectedSet.has(code);
            const isDeliveredAny = allRoundsSet.has(code);
            l.setStyle({
              weight: isSelected ? 2 : isDeliveredAny ? 1.2 : 0.5,
              color: isSelected ? '#0284c7' : isDeliveredAny ? '#be185d' : '#334155'
            });
          });

          l.on('click', () => {
            if (onZipClick && code) {
              onZipClick(code);
            }
          });
        }
      }).addTo(map);

      geoJsonLayerRef.current = layer;

      try {
        if (selectedSet.size > 0) {
          const matchingLayers = layer.getLayers().filter(l => {
            const c = String(l.feature?.properties?.plz || l.feature?.properties?.zip || '');
            return selectedSet.has(c);
          });
          if (matchingLayers.length > 0) {
            const group = L.featureGroup(matchingLayers);
            map.fitBounds(group.getBounds(), { padding: [30, 30], maxZoom: 11 });
          }
        } else if (allRoundsSet.size > 0) {
          const deliveredLayers = layer.getLayers().filter(l => {
            const c = String(l.feature?.properties?.plz || l.feature?.properties?.zip || '');
            return allRoundsSet.has(c);
          });
          if (deliveredLayers.length > 0) {
            const group = L.featureGroup(deliveredLayers);
            map.fitBounds(group.getBounds(), { padding: [30, 30], maxZoom: 11 });
          }
        } else {
          map.setView([46.65, 7.05], 9);
        }
      } catch (e) {
        map.setView([46.65, 7.05], 9);
      }
    }
  }, [geoData, selectedNpas, allRoundsNpas, onZipClick]);

  return (
    <div style={{ position: 'relative', width: '100%', height, borderRadius: '12px', overflow: 'hidden', border: '1px solid #334155' }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%', background: '#0f172a' }} />
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        background: 'rgba(15, 23, 42, 0.85)',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '0.72rem',
        color: '#94a3b8',
        zIndex: 1000,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>📍 <strong>{selectedNpas.length > 0 ? selectedNpas.length : allRoundsNpas.length}</strong> zones NPA</span>
        <span style={{ color: '#64748b' }}>| Cliquez sur un NPA pour filtrer</span>
      </div>
    </div>
  );
}

// Surface options matching prototype
const SURFACE_OPTIONS = [
  { value: 'Colis / Caisse', label: 'Colis / Caisse' },
  { value: 'Roll', label: 'Roll' },
  { value: 'Demi-Palette', label: 'Demi-Palette (0.5)' },
  { value: 'Palette Europe', label: 'Palette Europe (1.0)' },
  { value: '2 Palettes', label: '2 Palettes Europe (2.0)' }
];

export default function ConnectPage({ currentUser }) {
  // Organization Name dynamically from currentUser or fallback
  const orgName = currentUser?.company_name || currentUser?.organization_name || currentUser?.company || 'Amstein SA';

  // Navigation tabs: 'routes' (Mes Tournées) | 'demands' (Demandes) | 'expedite' (Expédier)
  const [activeTab, setActiveTab] = useState('routes');

  // Expedite sub-view: 'discovery' (Tournées disponibles) | 'create' (Nouvelle expédition) | 'orders' (Mes expéditions)
  const [expediteSubView, setExpediteSubView] = useState('discovery');

  // Expedite creation step: 'entry' (stops input) | 'assign' (round assignment)
  const [expediteStep, setExpediteStep] = useState('entry');

  // Active Selected Route for Detailed View (null = grid view)
  const [selectedRouteDetail, setSelectedRouteDetail] = useState(null);

  // Past rounds accordion expansion
  const [pastExpanded, setPastExpanded] = useState(false);

  // Hovered / Selected Route in Expedite Split View
  const [hoveredRouteId, setHoveredRouteId] = useState(null);

  // Filters in Expedite Discovery
  const [discoveryFilterZip, setDiscoveryFilterZip] = useState('');

  // Data from backend
  const [connectedJobs, setConnectedJobs] = useState([]);
  const [connectedOrders, setConnectedOrders] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [dbClients, setDbClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Simulation modal state
  const [simulationOrder, setSimulationOrder] = useState(null);
  const [simulationData, setSimulationData] = useState(null);
  const [isSimulatingCosts, setIsSimulatingCosts] = useState(false);

  // Offer proposing state on demands
  const [offeringOrderId, setOfferingOrderId] = useState(null);
  const [proposedPrice, setProposedPrice] = useState('48.00');

  // Final delivery price modal/form
  const [deliveringOrderId, setDeliveringOrderId] = useState(null);
  const [finalPriceInput, setFinalPriceInput] = useState('');

  // Expanded demands state
  const [expandedDemandIds, setExpandedDemandIds] = useState([6]);

  // Modal States
  const [showNewTourneeModal, setShowNewTourneeModal] = useState(false);
  const [editingTournee, setEditingTournee] = useState(null);
  const [duplicatingTournee, setDuplicatingTournee] = useState(null);

  // Round Picker Sheet Modal for a specific stop during Expedition
  const [pickerStopIndex, setPickerStopIndex] = useState(null);

  // Form State for Tournée (Connected Job)
  const [tourneeForm, setTourneeForm] = useState({
    job_id: '',
    name: '',
    description: '',
    price_per_stop: 24.0,
    planned_date: '2026-08-21',
    planned_start_time: '07:30',
    planned_end_time: '17:00',
    max_weight_per_stop: 25.0,
    max_packages_per_stop: 8,
    max_pallets_per_stop: 2,
    volume_condition: 'Colis / Caisse',
    special_instructions: 'Livraisons matinales prioritaires. Hayon recommandé. Température dirigée (Froid positif 2-4°C).',
    auto_accept: false,
    postal_codes_input: ''
  });

  // Dynamic Stops List for Expedite Creation Workflow (starts completely empty)
  const [expediteStops, setExpediteStops] = useState([]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/connect/bootstrap');
      if (res.ok) {
        const data = await res.json();
        setConnectedJobs(data.connected_jobs || []);
        setConnectedOrders(data.connected_transport_orders || []);
        setAvailableJobs(data.available_jobs || []);
      }
      // Load DB clients for autocomplete
      const clientsRes = await fetch('/api/clients');
      if (clientsRes.ok) {
        const cData = await clientsRes.json();
        setDbClients(Array.isArray(cData) ? cData : []);
      }
    } catch (err) {
      console.error('Error loading Connect data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSimulateCosts = async (order, targetJobId = null) => {
    setSimulationOrder(order);
    setIsSimulatingCosts(true);
    try {
      const res = await fetch(`/api/connected-transport-orders/${order.id}/simulate-costs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(targetJobId ? { target_job_id: targetJobId } : {})
      });
      if (res.ok) {
        const data = await res.json();
        setSimulationData(data.simulation || null);
      } else {
        const err = await res.json();
        alert(`Erreur de simulation : ${err.error || err.details || 'Impossible de calculer les coûts'}`);
        if (!simulationData) setSimulationOrder(null);
      }
    } catch (err) {
      console.error('Error simulating order costs:', err);
      alert('Erreur réseau lors de la simulation des coûts.');
      if (!simulationData) setSimulationOrder(null);
    } finally {
      setIsSimulatingCosts(false);
    }
  };

  const handleAcceptAndIntegrate = async (orderId, targetJobId = null) => {
    try {
      const res = await fetch(`/api/connected-transport-orders/${orderId}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(targetJobId ? { target_job_id: targetJobId } : {})
      });
      if (res.ok) {
        const result = await res.json();
        alert(result.message || 'Commande mutualisée acceptée et arrêts intégrés et re-séquencés avec succès !');
        setSimulationOrder(null);
        setSimulationData(null);
        await loadData();
      } else {
        const err = await res.json();
        alert(`Erreur : ${err.error || err.details || 'Échec de l\'acceptation'}`);
      }
    } catch (err) {
      console.error('Error accepting order:', err);
      alert('Erreur réseau lors de l\'acceptation de la commande.');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter planned jobs available for publishing
  const plannedJobs = useMemo(() => {
    return availableJobs.filter(j => j.status === 'planned' || j.status === 'in_progress' || !j.status);
  }, [availableJobs]);

  // Partition upcoming vs past connected jobs
  const { upcomingMissions, pastMissions } = useMemo(() => {
    const upcoming = [];
    const past = [];

    connectedJobs.forEach(job => {
      if (job.description && job.description.includes('Past')) {
        past.push(job);
      } else {
        upcoming.push(job);
      }
    });

    return { upcomingMissions: upcoming, pastMissions: past };
  }, [connectedJobs]);

  // Aggregate of all covered postal codes across all open/active rounds
  const allCoveredPostalCodes = useMemo(() => {
    const set = new Set();
    connectedJobs.forEach(j => {
      (j.postal_codes || []).forEach(n => {
        const code = String(n.postal_code || n).trim();
        if (code) set.add(code);
      });
    });
    return Array.from(set);
  }, [connectedJobs]);

  // When user selects a job in the publication modal, auto-fill all parameters
  const handleSelectJobForPublication = (selectedJobId) => {
    const job = availableJobs.find(j => String(j.id) === String(selectedJobId));
    if (!job) {
      setTourneeForm(prev => ({ ...prev, job_id: selectedJobId }));
      return;
    }

    const npas = Array.isArray(job.postal_codes) && job.postal_codes.length > 0
      ? job.postal_codes.join(', ')
      : '1731, 1070, 1071, 1525, 1552, 1553, 1607, 1614, 1615, 1616';

    const depTime = job.estimated_departure || '07:30';

    setTourneeForm(prev => ({
      ...prev,
      job_id: job.id,
      name: job.display_name || job.round_name || `Tournée #${job.id}`,
      description: `${job.round_name || 'Tournée'}`,
      planned_date: '2026-08-21',
      planned_start_time: depTime,
      planned_end_time: '17:00',
      postal_codes_input: npas,
      price_per_stop: prev.price_per_stop || 24.0
    }));
  };

  const handleToggleDemandExpand = (id) => {
    setExpandedDemandIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleAcceptOrder = async (orderId) => {
    try {
      const res = await fetch(`/api/connected-transport-orders/${orderId}/accept`, { method: 'POST' });
      if (res.ok) {
        await loadData();
      }
    } catch (err) {
      console.error('Error accepting order:', err);
    }
  };

  const handleRejectOrder = async (orderId) => {
    if (!confirm('Refuser cette demande de transport mutualisé ?')) return;
    try {
      const res = await fetch(`/api/connected-transport-orders/${orderId}/reject`, { method: 'POST' });
      if (res.ok) {
        await loadData();
      }
    } catch (err) {
      console.error('Error rejecting order:', err);
    }
  };

  const handleSendOffer = async (orderId) => {
    try {
      const res = await fetch(`/api/connected-transport-orders/${orderId}/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counter_offer_price: parseFloat(proposedPrice) })
      });
      if (res.ok) {
        alert(`Offre de ${proposedPrice} CHF transmise avec succès !`);
        setOfferingOrderId(null);
        await loadData();
      }
    } catch (err) {
      console.error('Error submitting offer:', err);
    }
  };

  const handleRespondToOffer = async (orderId, action) => {
    try {
      const res = await fetch(`/api/connected-transport-orders/${orderId}/respond-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      if (res.ok) {
        await loadData();
      }
    } catch (err) {
      console.error('Error responding to offer:', err);
    }
  };

  const handleMarkDelivered = async (orderId) => {
    try {
      const res = await fetch(`/api/connected-transport-orders/${orderId}/deliver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ final_price: finalPriceInput ? parseFloat(finalPriceInput) : null })
      });
      if (res.ok) {
        setDeliveringOrderId(null);
        await loadData();
      }
    } catch (err) {
      console.error('Error delivering order:', err);
    }
  };

  const handleDownloadCsv = (order) => {
    const stops = order.stops || [];
    let csvContent = "data:text/csv;charset=utf-8,Numero,Client,Type,Adresse,NPA,Ville,Poids_kg,Colis,Notes\n";
    if (stops.length > 0) {
      stops.forEach(s => {
        csvContent += `"${order.id}","${s.client_name || order.customer_name}","${s.type}","${s.address}","${s.postal_code || ''}","${s.city || ''}","${s.weight_kg || 1}","${s.packages_count || 1}","${s.description || order.notes || ''}"\n`;
      });
    } else {
      csvContent += `"${order.id}","${order.customer_name}","pickup","${order.pickup_address || 'Puidoux'}","1070","Puidoux","1","1","${order.notes || ''}"\n`;
      csvContent += `"${order.id}","SQTS","delivery","${order.delivery_address || 'Saint-Aubin'}","1566","Saint-Aubin","1","1","${order.notes || ''}"\n`;
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `loops_commande_${order.id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveTournee = async (e) => {
    e.preventDefault();
    if (!tourneeForm.job_id && !editingTournee && !duplicatingTournee) {
      alert('Veuillez sélectionner un Job planifié.');
      return;
    }

    const npas = tourneeForm.postal_codes_input
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(code => ({ postal_code: code }));

    const startIso = `${tourneeForm.planned_date}T${tourneeForm.planned_start_time}:00Z`;
    const endIso = `${tourneeForm.planned_date}T${tourneeForm.planned_end_time}:00Z`;

    const payload = {
      job_id: tourneeForm.job_id ? parseInt(tourneeForm.job_id) : (editingTournee ? editingTournee.job_id : null),
      target_job_id: tourneeForm.job_id ? parseInt(tourneeForm.job_id) : null,
      name: tourneeForm.name,
      description: tourneeForm.description || '',
      price_per_stop: parseFloat(tourneeForm.price_per_stop || 24.0),
      planned_start: startIso,
      planned_end: endIso,
      start_datetime: startIso,
      end_datetime: endIso,
      max_weight_per_stop: parseFloat(tourneeForm.max_weight_per_stop || 25.0),
      max_packages_per_stop: parseInt(tourneeForm.max_packages_per_stop || 8),
      max_pallets_per_stop: parseInt(tourneeForm.max_pallets_per_stop || 2),
      volume_condition: tourneeForm.volume_condition || 'Colis / Caisse',
      special_instructions: tourneeForm.special_instructions || '',
      auto_accept: Boolean(tourneeForm.auto_accept),
      postal_codes: npas
    };

    try {
      let res;
      if (duplicatingTournee) {
        res = await fetch(`/api/connected-jobs/${duplicatingTournee.id}/duplicate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else if (editingTournee) {
        res = await fetch(`/api/connected-jobs/${editingTournee.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch('/api/connected-jobs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Erreur lors de la publication');
      }

      setShowNewTourneeModal(false);
      setEditingTournee(null);
      setDuplicatingTournee(null);
      await loadData();
    } catch (err) {
      console.error('Error saving mission:', err);
      alert(`Erreur: ${err.message}`);
    }
  };

  const handleDeleteTournee = async (id) => {
    if (!confirm('Supprimer cette mission de la mutualisation ?')) return;
    try {
      await fetch(`/api/connected-jobs/${id}`, { method: 'DELETE' });
      await loadData();
    } catch (err) {
      console.error('Error deleting mission:', err);
    }
  };

  // Expedite Stop Management Helpers
  const handleAddStop = (type) => {
    const newStop = {
      id: `stop-${Date.now()}`,
      type,
      client_name: '',
      street: '',
      zip: '',
      city: '',
      weight: '',
      ground_area: 'Colis / Caisse',
      description: '',
      dependency_idx: type === 'DELIVERY' && expediteStops.filter(s => s.type === 'PICKUP').length > 0 ? 0 : null,
      round_id: '',
      budget_max: ''
    };
    setExpediteStops(prev => [...prev, newStop]);
  };

  const handleUpdateStop = (id, field, value) => {
    setExpediteStops(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleRemoveStop = (id) => {
    setExpediteStops(prev => prev.filter(s => s.id !== id));
  };

  const handleFillMyAddress = (stopId) => {
    handleUpdateStop(stopId, 'client_name', orgName);
    handleUpdateStop(stopId, 'street', 'Route de Lausanne 10');
    handleUpdateStop(stopId, 'zip', '1020');
    handleUpdateStop(stopId, 'city', 'Renens');
  };

  // Helper to check coverage and categorize rounds for a given stop
  const getRoundOptionsForStop = (stop) => {
    const stopZip = String(stop.zip || '').trim();
    const stopWeight = parseFloat(stop.weight || 0);

    return connectedJobs.map(job => {
      const jobZips = new Set((job.postal_codes || []).map(n => String(n.postal_code || n).trim()));
      const servesZip = jobZips.has(stopZip);
      const meetsWeight = stopWeight <= parseFloat(job.max_weight_per_stop || 25);
      const isStandard = servesZip && meetsWeight;

      return {
        round: job,
        roundId: job.id,
        name: job.name,
        transporterName: orgName,
        dateStr: formatShortDate(job.planned_start || job.start_datetime),
        price: parseFloat(job.price_per_stop || 24.0),
        isStandard,
        servesZip,
        meetsWeight
      };
    });
  };

  // Pre-assign best matching rounds when entering 'assign' step
  const handleContinueToAssign = () => {
    if (expediteStops.length === 0) {
      alert("Ajoutez au moins un arrêt.");
      return;
    }

    // Auto-assign first compatible round or fallback to first round
    setExpediteStops(prev => prev.map(s => {
      if (s.round_id) return s;
      const opts = getRoundOptionsForStop(s);
      const standard = opts.find(o => o.isStandard);
      const chosen = standard || opts[0];
      return {
        ...s,
        round_id: chosen ? String(chosen.roundId) : (connectedJobs[0] ? String(connectedJobs[0].id) : '')
      };
    }));

    setExpediteStep('assign');
  };

  // Total calculated price in Step 2
  const { totalOrderPrice, hasSpecialRequests } = useMemo(() => {
    let total = 0;
    let special = false;

    expediteStops.forEach(s => {
      const job = connectedJobs.find(j => String(j.id) === String(s.round_id));
      const price = job ? parseFloat(job.price_per_stop || 24.0) : 24.0;
      total += price;

      if (s.budget_max) {
        special = true;
      } else if (job) {
        const jobZips = new Set((job.postal_codes || []).map(n => String(n.postal_code || n).trim()));
        if (!jobZips.has(String(s.zip).trim())) {
          special = true;
        }
      }
    });

    return { totalOrderPrice: total, hasSpecialRequests: special };
  }, [expediteStops, connectedJobs]);

  const handleSubmitExpediteOrder = async () => {
    try {
      const payloadStops = expediteStops.map(s => {
        const job = connectedJobs.find(j => String(j.id) === String(s.round_id));
        const jobZips = new Set((job?.postal_codes || []).map(n => String(n.postal_code || n).trim()));
        const isSpecial = !jobZips.has(String(s.zip).trim()) || parseFloat(s.weight || 0) > parseFloat(job?.max_weight_per_stop || 25);

        return {
          type: s.type.toLowerCase(),
          client_name: s.client_name || 'Client',
          street: s.street,
          address: `${s.street}, ${s.zip} ${s.city}`.trim(),
          postal_code: s.zip,
          city: s.city,
          weight_kg: parseFloat(s.weight || 1.0),
          ground_area: s.ground_area,
          packages_count: 1,
          description: s.description || 'Colis',
          dependency_idx: s.dependency_idx,
          round_id: s.round_id ? parseInt(s.round_id) : null,
          budget_max: s.budget_max ? parseFloat(s.budget_max) : null,
          price_chf: job ? parseFloat(job.price_per_stop || 24.0) : 24.0,
          is_special: isSpecial
        };
      });

      const primaryClient = expediteStops[0]?.client_name || orgName;

      const res = await fetch('/api/connected-transport-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: primaryClient,
          sender_company_name: orgName,
          notes: expediteStops.map(s => s.description).filter(Boolean).join(' // '),
          total_price: totalOrderPrice,
          stops: payloadStops
        })
      });

      if (res.ok) {
        alert("Votre expédition a été validée et transmise avec succès !");
        setExpediteStops([]);
        setExpediteStep('entry');
        setExpediteSubView('orders');
        await loadData();
      }
    } catch (err) {
      console.error('Error submitting expedition:', err);
    }
  };

  const pendingDemandsCount = connectedOrders.filter(o => o.status === 'pending').length;

  const filteredDiscoveryJobs = useMemo(() => {
    return connectedJobs.filter(job => {
      if (discoveryFilterZip) {
        const matchesZip = (job.postal_codes || []).some(n =>
          (n.postal_code || n).includes(discoveryFilterZip.trim()) ||
          (n.city || '').toLowerCase().includes(discoveryFilterZip.trim().toLowerCase())
        );
        if (!matchesZip) return false;
      }
      return true;
    });
  }, [connectedJobs, discoveryFilterZip]);

  const activeShipperRoute = useMemo(() => {
    if (hoveredRouteId) return connectedJobs.find(j => j.id === hoveredRouteId);
    return filteredDiscoveryJobs[0] || connectedJobs[0] || null;
  }, [hoveredRouteId, filteredDiscoveryJobs, connectedJobs]);

  return (
    <div className="connect-page-root" style={{
      height: '100%',
      maxHeight: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#0b1329',
      color: '#f8fafc',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      overflow: 'hidden'
    }}>
      {/* ------------------------------------------------------------- */}
      {/* 1. TOP HEADER & MAIN NAVIGATION TABS                          */}
      {/* ------------------------------------------------------------- */}
      <div style={{
        background: '#131e36',
        borderBottom: '1px solid #1e293b',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '52px',
        flexShrink: 0
      }}>
        {/* Clean 3 Navigation Tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
          <button
            onClick={() => { setActiveTab('routes'); setSelectedRouteDetail(null); }}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'routes' ? '#38bdf8' : '#94a3b8',
              fontWeight: activeTab === 'routes' ? '700' : '500',
              fontSize: '0.9rem',
              padding: '0 14px',
              height: '100%',
              cursor: 'pointer',
              borderBottom: activeTab === 'routes' ? '3px solid #38bdf8' : '3px solid transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Truck size={16} />
            <span>Mes Tournées</span>
          </button>

          <button
            onClick={() => { setActiveTab('demands'); setSelectedRouteDetail(null); }}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'demands' ? '#38bdf8' : '#94a3b8',
              fontWeight: activeTab === 'demands' ? '700' : '500',
              fontSize: '0.9rem',
              padding: '0 14px',
              height: '100%',
              cursor: 'pointer',
              borderBottom: activeTab === 'demands' ? '3px solid #38bdf8' : '3px solid transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FileText size={16} />
            <span>Demandes</span>
            {pendingDemandsCount > 0 && (
              <span style={{
                background: '#ef4444',
                color: '#fff',
                borderRadius: '10px',
                padding: '1px 6px',
                fontSize: '0.72rem',
                fontWeight: '800'
              }}>
                {pendingDemandsCount}
              </span>
            )}
          </button>

          <button
            onClick={() => { setActiveTab('expedite'); setSelectedRouteDetail(null); }}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'expedite' ? '#38bdf8' : '#94a3b8',
              fontWeight: activeTab === 'expedite' ? '700' : '500',
              fontSize: '0.9rem',
              padding: '0 14px',
              height: '100%',
              cursor: 'pointer',
              borderBottom: activeTab === 'expedite' ? '3px solid #38bdf8' : '3px solid transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Package size={16} />
            <span>Expédier</span>
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* 2. BODY CONTENT                                               */}
      {/* ------------------------------------------------------------- */}
      <div className="connect-page-body" style={{ flex: 1, minHeight: 0, padding: '24px 32px', overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ========================================================= */}
        {/* TAB 1: MES TOURNÉES (GRID OR DETAIL)                      */}
        {/* ========================================================= */}
        {activeTab === 'routes' && (
          <div>
            {!selectedRouteDetail ? (
              /* GRID OF TOURNÉES */
              <div>
                {/* Clean Action Toolbar */}
                <div style={{
                  background: 'rgba(30, 41, 59, 0.6)',
                  border: '1px solid #1e293b',
                  borderRadius: '14px',
                  padding: '12px 18px',
                  marginBottom: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '0.86rem' }}>
                    <Truck size={16} color="#38bdf8" />
                    <span><strong>{upcomingMissions.length}</strong> mission{upcomingMissions.length > 1 ? 's' : ''} mutualisée{upcomingMissions.length > 1 ? 's' : ''} active{upcomingMissions.length > 1 ? 's' : ''}</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={loadData}
                      style={{
                        background: '#1e293b',
                        border: '1px solid #334155',
                        color: '#f8fafc',
                        borderRadius: '8px',
                        padding: '8px 14px',
                        fontSize: '0.82rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <RefreshCw size={14} className={isLoading ? 'spin' : ''} />
                      <span>Actualiser</span>
                    </button>

                    <button
                      onClick={() => {
                        setEditingTournee(null);
                        setDuplicatingTournee(null);
                        const firstJob = plannedJobs[0];
                        if (firstJob) {
                          handleSelectJobForPublication(firstJob.id);
                        } else {
                          setTourneeForm({
                            job_id: '',
                            name: '',
                            description: '',
                            price_per_stop: 24.0,
                            planned_date: '2026-08-21',
                            planned_start_time: '07:30',
                            planned_end_time: '17:00',
                            max_weight_per_stop: 25.0,
                            max_packages_per_stop: 8,
                            max_pallets_per_stop: 2,
                            volume_condition: 'Colis / Caisse',
                            special_instructions: 'Livraisons matinales prioritaires. Hayon recommandé.',
                            auto_accept: false,
                            postal_codes_input: '1731, 1070, 1071, 1525, 1552, 1553, 1607, 1614, 1615, 1616'
                          });
                        }
                        setShowNewTourneeModal(true);
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
                        border: 'none',
                        color: '#0f172a',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '0.85rem',
                        fontWeight: '800',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 4px 14px rgba(56, 189, 248, 0.3)'
                      }}
                    >
                      <Plus size={16} />
                      <span>Publier une mission</span>
                    </button>
                  </div>
                </div>

                {/* 3-Column Tournées Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '20px'
                }}>
                  {upcomingMissions.map(job => {
                    const npas = job.postal_codes || [];
                    const scheduleStr = formatPlannedSchedule(job.planned_start || job.start_datetime, job.planned_end || job.end_datetime);

                    return (
                      <div
                        key={job.id}
                        onClick={() => setSelectedRouteDetail(job)}
                        style={{
                          background: '#131e36',
                          border: '1px solid #233354',
                          borderRadius: '14px',
                          padding: '18px 20px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 4px 14px rgba(0,0,0,0.25)'
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#38bdf8'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#233354'}
                      >
                        {/* Top Card Row */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#94a3b8', fontSize: '0.78rem' }}>
                            <Calendar size={14} />
                            <span>{scheduleStr}</span>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => {
                                setEditingTournee(job);
                                setDuplicatingTournee(null);
                                setTourneeForm({
                                  job_id: job.job_id || '',
                                  name: job.name,
                                  description: job.description || '',
                                  price_per_stop: job.price_per_stop || 24.0,
                                  planned_date: job.planned_start ? job.planned_start.slice(0, 10) : '2026-08-21',
                                  planned_start_time: job.planned_start ? job.planned_start.slice(11, 16) : '07:30',
                                  planned_end_time: job.planned_end ? job.planned_end.slice(11, 16) : '17:00',
                                  max_weight_per_stop: job.max_weight_per_stop || 25.0,
                                  max_packages_per_stop: job.max_packages_per_stop || 8,
                                  max_pallets_per_stop: job.max_pallets_per_stop || 2,
                                  volume_condition: job.volume_condition || 'Colis / Caisse',
                                  special_instructions: job.special_instructions || '',
                                  auto_accept: Boolean(job.auto_accept),
                                  postal_codes_input: (job.postal_codes || []).map(n => n.postal_code || n).join(', ')
                                });
                                setShowNewTourneeModal(true);
                              }}
                              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}
                              title="Modifier"
                            >
                              <Edit2 size={13} />
                            </button>
                            <span style={{
                              background: 'rgba(34, 197, 94, 0.15)',
                              color: '#22c55e',
                              borderRadius: '6px',
                              padding: '2px 8px',
                              fontSize: '0.7rem',
                              fontWeight: '700'
                            }}>
                              Active
                            </span>
                            <button
                              onClick={() => handleDeleteTournee(job.id)}
                              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '2px' }}
                              title="Supprimer"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>

                        {/* Route Title */}
                        <div>
                          <h3 style={{ margin: '0', fontSize: '1.05rem', fontWeight: '800', color: '#f8fafc' }}>
                            {job.name}
                          </h3>
                        </div>

                        {/* Postal Code Badges (Pills) */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {npas.slice(0, 6).map((npa, idx) => (
                            <span
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDiscoveryFilterZip(npa.postal_code || npa);
                                setActiveTab('expedite');
                                setExpediteSubView('discovery');
                              }}
                              style={{
                                background: 'rgba(168, 85, 247, 0.15)',
                                color: '#c084fc',
                                border: '1px solid rgba(168, 85, 247, 0.3)',
                                borderRadius: '6px',
                                padding: '2px 7px',
                                fontSize: '0.72rem',
                                fontWeight: '700',
                                cursor: 'pointer'
                              }}
                              title="Cliquer pour filtrer les tournées sur ce NPA"
                            >
                              {npa.postal_code || npa}
                            </span>
                          ))}
                          {npas.length > 6 && (
                            <span style={{
                              background: 'rgba(148, 163, 184, 0.12)',
                              color: '#94a3b8',
                              borderRadius: '6px',
                              padding: '2px 7px',
                              fontSize: '0.72rem',
                              fontWeight: '700'
                            }}>
                              +{npas.length - 6}
                            </span>
                          )}
                        </div>

                        {/* Footer Row */}
                        <div style={{
                          borderTop: '1px solid #1e293b',
                          paddingTop: '10px',
                          marginTop: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
                            1 commande associée
                          </span>
                          <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#22c55e' }}>
                            {Number(job.price_per_stop || 24).toFixed(2)} CHF <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#94a3b8' }}>/ arrêt</span>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Past Missions Accordion */}
                <div style={{ marginTop: '36px', borderTop: '1px solid #1e293b', paddingTop: '16px' }}>
                  <button
                    onClick={() => setPastExpanded(!pastExpanded)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#94a3b8',
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: 0
                    }}
                  >
                    <span>Tournées passées (4)</span>
                    {pastExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {pastExpanded && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: '16px',
                      marginTop: '14px',
                      opacity: 0.75
                    }}>
                      {[1, 2, 3, 4].map(idx => (
                        <div key={`past-${idx}`} style={{
                          background: '#10182b',
                          border: '1px solid #1e293b',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: '0.74rem' }}>
                            <span>0{idx} Aug 2026, 07:30 - 17:00</span>
                            <span style={{ background: 'rgba(148, 163, 184, 0.1)', color: '#94a3b8', padding: '1px 6px', borderRadius: '4px' }}>Passée</span>
                          </div>
                          <h4 style={{ margin: 0, fontSize: '0.92rem', color: '#cbd5e1' }}>Fribourg - Lausanne - Genève</h4>
                          <div style={{ color: '#64748b', fontSize: '0.78rem' }}>24.00 CHF / arrêt · 0 commandes</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* DETAILED VIEW OF A TOURNÉE */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <button
                      onClick={() => setSelectedRouteDetail(null)}
                      style={{
                        background: '#1e293b',
                        border: '1px solid #334155',
                        color: '#f8fafc',
                        borderRadius: '8px',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <ArrowLeft size={18} />
                    </button>
                    <div>
                      <h2 style={{ margin: '0 0 2px 0', fontSize: '1.25rem', fontWeight: '800', color: '#f8fafc' }}>
                        Détails de la tournée
                      </h2>
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                        {selectedRouteDetail.name} · {formatPlannedSchedule(selectedRouteDetail.planned_start || selectedRouteDetail.start_datetime, selectedRouteDetail.planned_end || selectedRouteDetail.end_datetime)}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={() => {
                        setEditingTournee(selectedRouteDetail);
                        setDuplicatingTournee(null);
                        setTourneeForm({
                          job_id: selectedRouteDetail.job_id || '',
                          name: selectedRouteDetail.name,
                          description: selectedRouteDetail.description || '',
                          price_per_stop: selectedRouteDetail.price_per_stop || 24.0,
                          planned_date: selectedRouteDetail.planned_start ? selectedRouteDetail.planned_start.slice(0, 10) : '2026-08-21',
                          planned_start_time: selectedRouteDetail.planned_start ? selectedRouteDetail.planned_start.slice(11, 16) : '07:30',
                          planned_end_time: selectedRouteDetail.planned_end ? selectedRouteDetail.planned_end.slice(11, 16) : '17:00',
                          max_weight_per_stop: selectedRouteDetail.max_weight_per_stop || 25.0,
                          max_packages_per_stop: selectedRouteDetail.max_packages_per_stop || 8,
                          max_pallets_per_stop: selectedRouteDetail.max_pallets_per_stop || 2,
                          volume_condition: selectedRouteDetail.volume_condition || 'Colis / Caisse',
                          special_instructions: selectedRouteDetail.special_instructions || '',
                          auto_accept: Boolean(selectedRouteDetail.auto_accept),
                          postal_codes_input: (selectedRouteDetail.postal_codes || []).map(n => n.postal_code || n).join(', ')
                        });
                        setShowNewTourneeModal(true);
                      }}
                      style={{
                        background: 'none',
                        border: '1px solid #38bdf8',
                        color: '#38bdf8',
                        borderRadius: '8px',
                        padding: '6px 14px',
                        fontSize: '0.8rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Edit2 size={13} />
                      <span>Modifier</span>
                    </button>

                    <button
                      onClick={() => {
                        setDuplicatingTournee(selectedRouteDetail);
                        setEditingTournee(null);
                        setTourneeForm({
                          job_id: '',
                          name: `${selectedRouteDetail.name} (Nouvelle date)`,
                          description: selectedRouteDetail.description || '',
                          price_per_stop: selectedRouteDetail.price_per_stop || 24.0,
                          planned_date: '2026-08-28',
                          planned_start_time: '07:30',
                          planned_end_time: '17:00',
                          max_weight_per_stop: selectedRouteDetail.max_weight_per_stop || 25.0,
                          max_packages_per_stop: selectedRouteDetail.max_packages_per_stop || 8,
                          max_pallets_per_stop: selectedRouteDetail.max_pallets_per_stop || 2,
                          volume_condition: selectedRouteDetail.volume_condition || 'Colis / Caisse',
                          special_instructions: selectedRouteDetail.special_instructions || '',
                          auto_accept: Boolean(selectedRouteDetail.auto_accept),
                          postal_codes_input: (selectedRouteDetail.postal_codes || []).map(n => n.postal_code || n).join(', ')
                        });
                        setShowNewTourneeModal(true);
                      }}
                      style={{
                        background: 'none',
                        border: '1px solid #22c55e',
                        color: '#22c55e',
                        borderRadius: '8px',
                        padding: '6px 14px',
                        fontSize: '0.8rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Copy size={13} />
                      <span>Dupliquer (autre mission / date)</span>
                    </button>

                    <span style={{
                      background: 'rgba(148, 163, 184, 0.15)',
                      color: '#94a3b8',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: '0.78rem'
                    }}>
                      Suppression bloquée (commandes liées)
                    </span>
                  </div>
                </div>

                {/* Tournée Overview Card */}
                <div style={{
                  background: '#131e36',
                  border: '1px solid #233354',
                  borderRadius: '16px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '18px'
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '1.2rem', fontWeight: '800' }}>
                      {selectedRouteDetail.name}
                    </h3>
                    <div style={{ fontSize: '0.82rem', color: '#cbd5e1', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={14} color="#38bdf8" />
                      <span>{formatPlannedSchedule(selectedRouteDetail.planned_start || selectedRouteDetail.start_datetime, selectedRouteDetail.planned_end || selectedRouteDetail.end_datetime)}</span>
                    </div>
                  </div>

                  {/* 2-Column Info Boxes */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{
                      background: '#0f172a',
                      border: '1px solid #1e293b',
                      borderRadius: '12px',
                      padding: '14px 16px'
                    }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px', marginBottom: '6px' }}>
                        💳 TARIFICATION DE LA TOURNÉE
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '900', color: '#22c55e' }}>
                        {selectedRouteDetail.price_per_stop || 24.00} CHF <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '500' }}>/ arrêt</span>
                      </div>
                    </div>

                    <div style={{
                      background: '#0f172a',
                      border: '1px solid #1e293b',
                      borderRadius: '12px',
                      padding: '14px 16px'
                    }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px', marginBottom: '6px' }}>
                        🛡️ CONDITIONS DE SERVICE
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#cbd5e1' }}>
                        Poids par arrêt : <strong style={{ color: '#fff' }}>≤ {selectedRouteDetail.max_weight_per_stop || 25} kg</strong>
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#cbd5e1', marginTop: '4px' }}>
                        Volume par arrêt : <strong style={{ color: '#fff' }}>≤ {selectedRouteDetail.volume_condition || 'Colis / Caisse'}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Special Instructions Note */}
                  {selectedRouteDetail.special_instructions && (
                    <div style={{
                      background: 'rgba(56, 189, 248, 0.08)',
                      border: '1px solid rgba(56, 189, 248, 0.25)',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      fontSize: '0.82rem',
                      color: '#cbd5e1'
                    }}>
                      <strong style={{ color: '#38bdf8' }}>📌 Instructions spécifiques : </strong>
                      {selectedRouteDetail.special_instructions}
                    </div>
                  )}

                  {/* NPA DESSERVIS */}
                  <div>
                    <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#c084fc', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MapPin size={14} />
                      <span>NPA DESSERVIS ({(selectedRouteDetail.postal_codes || []).length})</span>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      maxHeight: '130px',
                      overflowY: 'auto',
                      padding: '8px',
                      background: '#0f172a',
                      borderRadius: '10px',
                      border: '1px solid #1e293b'
                    }}>
                      {(selectedRouteDetail.postal_codes || []).map((n, idx) => (
                        <span
                          key={idx}
                          style={{
                            background: 'rgba(168, 85, 247, 0.15)',
                            color: '#c084fc',
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '6px',
                            padding: '3px 8px',
                            fontSize: '0.72rem',
                            fontWeight: '700'
                          }}
                        >
                          {n.postal_code || n} {n.city || ''}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Map of Covered Zones */}
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '0.92rem', fontWeight: '700', color: '#94a3b8' }}>
                    Zones desservies
                  </h4>
                  <ConnectPostalMap
                    selectedNpas={(selectedRouteDetail.postal_codes || []).map(n => n.postal_code || n)}
                    allRoundsNpas={allCoveredPostalCodes}
                    onZipClick={(zip) => {
                      setDiscoveryFilterZip(zip);
                      setActiveTab('expedite');
                      setExpediteSubView('discovery');
                    }}
                    height="280px"
                  />
                </div>

                {/* 3 KPI Stat Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  <div style={{
                    background: '#131e36',
                    border: '1px solid #233354',
                    borderRadius: '14px',
                    padding: '18px 20px'
                  }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#22c55e', letterSpacing: '0.5px' }}>
                      REVENU RÉALISÉ
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#22c55e', margin: '4px 0' }}>
                      0.00 CHF
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      commandes livrées uniquement
                    </div>
                  </div>

                  <div style={{
                    background: '#131e36',
                    border: '1px solid #233354',
                    borderRadius: '14px',
                    padding: '18px 20px'
                  }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px' }}>
                      COMMANDES
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#fff', margin: '4px 0' }}>
                      1 <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#eab308' }}>1 att.</span> <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#22c55e' }}>0 conf.</span>
                    </div>
                  </div>

                  <div style={{
                    background: '#131e36',
                    border: '1px solid #233354',
                    borderRadius: '14px',
                    padding: '18px 20px'
                  }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px' }}>
                      POIDS TOTAL
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#fff', margin: '4px 0' }}>
                      2 <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#94a3b8' }}>kg</span>
                    </div>
                  </div>
                </div>

                {/* Commandes Associées */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc' }}>
                    Commandes associées (1)
                  </h4>

                  <div style={{
                    background: '#131e36',
                    border: '1px solid #233354',
                    borderRadius: '12px',
                    padding: '14px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => setActiveTab('demands')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontWeight: '800', color: '#38bdf8' }}>#6</span>
                      <span style={{ fontWeight: '700', color: '#fff' }}>{orgName}</span>
                      <span style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: '700' }}>
                        P {orgName} (1kg)
                      </span>
                      <span style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: '700' }}>
                        L SQTS (1kg)
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ background: 'rgba(234, 179, 8, 0.15)', color: '#eab308', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: '700' }}>
                        En attente
                      </span>
                      <ChevronRight size={16} color="#94a3b8" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 2: DEMANDES SPÉCIALES (Dynamic Database Orders)       */}
        {/* ========================================================= */}
        {activeTab === 'demands' && (
          <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', fontWeight: '800', color: '#f59e0b' }}>
              <span>●</span>
              <span>Demandes spéciales ({connectedOrders.length})</span>
            </div>

            {/* List all connected orders from database */}
            {connectedOrders.map(order => {
              const isExpanded = expandedDemandIds.includes(order.id);
              const pickupStops = (order.stops || []).filter(s => s.type === 'pickup');
              const deliveryStops = (order.stops || []).filter(s => s.type === 'delivery');
              const routeName = order.notes?.includes('|') ? order.notes.split('|')[0].trim() : (order.stops?.[0]?.connected_job_name || 'Fribourg - Vevey - Sierre');
              const totalRev = Number(order.revenue || 48.00).toFixed(2);
              const halfRev = (Number(order.revenue || 48.00) / (order.stops?.length || 2)).toFixed(2);

              return (
                <div
                  key={order.id}
                  style={{
                    background: '#131e36',
                    border: '1px solid #233354',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
                  }}
                >
                  {/* Header Row */}
                  <div
                    onClick={() => handleToggleDemandExpand(order.id)}
                    style={{
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      borderBottom: isExpanded ? '1px solid #1e293b' : 'none'
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: '6px', padding: '2px 8px', fontSize: '0.76rem', fontWeight: '800' }}>
                          #{order.id}
                        </span>
                        <strong style={{ fontSize: '0.95rem', color: '#fff' }}>{order.customer_name}</strong>
                        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '17 Aug, 21:26'}
                        </span>
                      </div>

                      <div style={{ fontSize: '0.85rem', color: '#cbd5e1', fontWeight: '600', marginBottom: '4px' }}>
                        {routeName}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.76rem' }}>
                        <span style={{ color: '#38bdf8' }}>{pickupStops.length || 1} Ramassage · {halfRev} CHF</span>
                        <span style={{ color: '#22c55e' }}>{deliveryStops.length || 1} Livraison · {halfRev} CHF</span>
                        {order.constraint_violations && order.constraint_violations.length > 0 && (
                          <span style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', borderRadius: '4px', padding: '1px 6px', fontWeight: '700' }}>
                            Ne respecte pas toutes vos conditions
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '1.1rem', fontWeight: '900', color: '#fff' }}>
                        {order.counter_offer_price ? `${Number(order.counter_offer_price).toFixed(2)} CHF (Offre)` : `${totalRev} CHF`}
                      </span>
                      <span style={{
                        background: order.status === 'accepted' ? 'rgba(34, 197, 94, 0.15)' : order.status === 'counter_offer' ? 'rgba(245, 158, 11, 0.15)' : order.status === 'delivered' ? 'rgba(56, 189, 248, 0.15)' : order.status === 'rejected' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                        color: order.status === 'accepted' ? '#22c55e' : order.status === 'counter_offer' ? '#f59e0b' : order.status === 'delivered' ? '#38bdf8' : order.status === 'rejected' ? '#ef4444' : '#eab308',
                        borderRadius: '6px',
                        padding: '3px 10px',
                        fontSize: '0.76rem',
                        fontWeight: '800'
                      }}>
                        {order.status === 'accepted' ? 'Acceptée' : order.status === 'counter_offer' ? 'Offre envoyée' : order.status === 'delivered' ? 'Livrée' : order.status === 'rejected' ? 'Refusée' : 'À traiter'}
                      </span>
                      {isExpanded ? <ChevronUp size={18} color="#94a3b8" /> : <ChevronDown size={18} color="#94a3b8" />}
                    </div>
                  </div>

                  {/* Expanded Details Body */}
                  {isExpanded && (
                    <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', background: '#0f172a' }}>
                      {/* Pickup Stop */}
                      {pickupStops.map((stop, sIdx) => (
                        <div
                          key={`p-${stop.id || sIdx}`}
                          style={{
                            background: '#131e36',
                            border: '1px solid #1e293b',
                            borderRadius: '12px',
                            padding: '14px 18px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: 'rgba(56, 189, 248, 0.2)',
                              color: '#38bdf8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '800',
                              fontSize: '0.85rem'
                            }}>
                              P
                            </div>
                            <div>
                              <div style={{ fontWeight: '800', color: '#fff', fontSize: '0.92rem' }}>{stop.client_name || order.customer_name}</div>
                              <div style={{ color: '#cbd5e1', fontSize: '0.82rem', marginTop: '2px' }}>{stop.address}</div>
                              <div style={{ color: '#94a3b8', fontSize: '0.76rem', marginTop: '4px' }}>
                                {stop.description || 'Analyse SQTS : froid négatif : 1x 330gr de glace + 2 gaufres // froid positif : 1 morceau de brebiclette, 1 yogourt'}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#94a3b8', fontSize: '0.74rem', marginTop: '6px' }}>
                                <Package size={13} />
                                <span>{stop.weight_kg || 1} kg · Colis / Caisse</span>
                              </div>
                            </div>
                          </div>

                          <span style={{ fontWeight: '800', color: '#fff', fontSize: '0.92rem' }}>
                            {halfRev} CHF
                          </span>
                        </div>
                      ))}

                      {/* Delivery Stop */}
                      {deliveryStops.map((stop, sIdx) => (
                        <div
                          key={`d-${stop.id || sIdx}`}
                          style={{
                            background: '#131e36',
                            border: '1px solid #1e293b',
                            borderRadius: '12px',
                            padding: '14px 18px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: 'rgba(34, 197, 94, 0.2)',
                              color: '#22c55e',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '800',
                              fontSize: '0.85rem'
                            }}>
                              L
                            </div>
                            <div>
                              <div style={{ fontWeight: '800', color: '#fff', fontSize: '0.92rem' }}>{stop.client_name || 'SQTS'}</div>
                              <div style={{ color: '#f87171', fontSize: '0.82rem', fontWeight: '700', marginTop: '2px' }}>
                                {stop.address}
                              </div>
                              <div style={{ color: '#94a3b8', fontSize: '0.76rem', marginTop: '4px' }}>
                                {stop.description || 'Analyse SQTS : froid négatif : 1x 330gr de glace + 2 gaufres // froid positif : 1 morceau de brebiclette, 1 yogourt'}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#94a3b8', fontSize: '0.74rem', marginTop: '6px' }}>
                                <Package size={13} />
                                <span>{stop.weight_kg || 1} kg · Colis / Caisse</span>
                              </div>
                            </div>
                          </div>

                          <span style={{ fontWeight: '800', color: '#fff', fontSize: '0.92rem' }}>
                            {halfRev} CHF
                          </span>
                        </div>
                      ))}

                      {/* Calculer les coûts pour cette commande */}
                      <button
                        onClick={() => handleSimulateCosts(order)}
                        style={{
                          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                          border: '1px solid #38bdf8',
                          color: '#fff',
                          borderRadius: '10px',
                          padding: '12px 16px',
                          fontSize: '0.86rem',
                          fontWeight: '800',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          boxShadow: '0 4px 14px rgba(2, 132, 199, 0.35)',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <Calculator size={17} />
                        <span>Calculer les coûts pour cette commande</span>
                      </button>

                      {/* -------------------------------------------------------- */}
                      {/* ACTION BUTTONS (Context-aware based on order status)     */}
                      {/* -------------------------------------------------------- */}

                      {/* 1. Pending status: Propose Offer, Refuse, Accept */}
                      {order.status === 'pending' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                          <button
                            onClick={() => setOfferingOrderId(offeringOrderId === order.id ? null : order.id)}
                            style={{
                              background: 'none',
                              border: '1px solid #f59e0b',
                              color: '#f59e0b',
                              borderRadius: '8px',
                              padding: '10px',
                              fontSize: '0.82rem',
                              fontWeight: '700',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            <DollarSign size={15} />
                            <span>Proposer une offre</span>
                          </button>

                          <button
                            onClick={() => handleRejectOrder(order.id)}
                            style={{
                              background: 'none',
                              border: '1px solid #ef4444',
                              color: '#ef4444',
                              borderRadius: '8px',
                              padding: '10px',
                              fontSize: '0.82rem',
                              fontWeight: '700',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            <X size={15} />
                            <span>Refuser</span>
                          </button>

                          <button
                            onClick={() => handleAcceptOrder(order.id)}
                            style={{
                              background: '#0284c7',
                              border: 'none',
                              color: '#fff',
                              borderRadius: '8px',
                              padding: '10px',
                              fontSize: '0.82rem',
                              fontWeight: '800',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            <Check size={15} />
                            <span>Accepter</span>
                          </button>
                        </div>
                      )}

                      {/* 2. Counter-offer sent: status info message */}
                      {order.status === 'counter_offer' && (
                        <div style={{
                          background: 'rgba(245, 158, 11, 0.1)',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          borderRadius: '8px',
                          padding: '10px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          color: '#f59e0b',
                          fontSize: '0.82rem',
                          fontWeight: '700'
                        }}>
                          <span>⏳ Offre de {Number(order.counter_offer_price).toFixed(2)} CHF transmise. En attente de la réponse de l'expéditeur.</span>
                          <button
                            onClick={() => setOfferingOrderId(offeringOrderId === order.id ? null : order.id)}
                            style={{ background: 'none', border: '1px solid #f59e0b', color: '#f59e0b', borderRadius: '6px', padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer' }}
                          >
                            Modifier l'offre
                          </button>
                        </div>
                      )}

                      {/* Offer Sub-card (when editing / creating counter offer) */}
                      {offeringOrderId === order.id && (
                        <div style={{
                          background: 'rgba(245, 158, 11, 0.08)',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          borderRadius: '10px',
                          padding: '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px'
                        }}>
                          <div style={{ fontSize: '0.82rem', fontWeight: '800', color: '#f59e0b' }}>
                            Votre offre
                          </div>
                          <label style={{ fontSize: '0.76rem', color: '#cbd5e1' }}>
                            Prix proposé (CHF)
                          </label>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <input
                              type="number"
                              step="0.5"
                              value={proposedPrice}
                              onChange={e => setProposedPrice(e.target.value)}
                              style={{
                                flex: 1,
                                background: '#0f172a',
                                border: '1px solid #334155',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                color: '#fff',
                                fontSize: '0.9rem',
                                fontWeight: '700'
                              }}
                            />
                            <button
                              onClick={() => handleSendOffer(order.id)}
                              style={{
                                background: '#f59e0b',
                                border: 'none',
                                color: '#0f172a',
                                borderRadius: '8px',
                                padding: '8px 18px',
                                fontWeight: '800',
                                cursor: 'pointer'
                              }}
                            >
                              Envoyer
                            </button>
                            <button
                              onClick={() => setOfferingOrderId(null)}
                              style={{
                                background: '#1e293b',
                                border: '1px solid #334155',
                                color: '#cbd5e1',
                                borderRadius: '8px',
                                padding: '8px 14px',
                                cursor: 'pointer'
                              }}
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      )}

                      {/* 3. Accepted status: Mark Delivered only */}
                      {order.status === 'accepted' && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                          <button
                            onClick={() => { setDeliveringOrderId(order.id); setFinalPriceInput(totalRev); }}
                            style={{
                              background: '#22c55e',
                              border: 'none',
                              color: '#0f172a',
                              borderRadius: '8px',
                              padding: '8px 18px',
                              fontSize: '0.82rem',
                              fontWeight: '800',
                              cursor: 'pointer'
                            }}
                          >
                            ✓ Marquer comme livré & facturé
                          </button>
                        </div>
                      )}

                      {deliveringOrderId === order.id && (
                        <div style={{
                          background: 'rgba(34, 197, 94, 0.08)',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          borderRadius: '10px',
                          padding: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <span style={{ fontSize: '0.82rem', color: '#22c55e', fontWeight: '700' }}>Prix final facturé (CHF) :</span>
                          <input
                            type="number"
                            step="0.5"
                            value={finalPriceInput}
                            onChange={e => setFinalPriceInput(e.target.value)}
                            style={{ width: '120px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontWeight: '700' }}
                          />
                          <button
                            onClick={() => handleMarkDelivered(order.id)}
                            style={{ background: '#22c55e', border: 'none', color: '#0f172a', borderRadius: '6px', padding: '6px 14px', fontWeight: '800', cursor: 'pointer' }}
                          >
                            Confirmer livraison
                          </button>
                        </div>
                      )}

                      {/* 4. Delivered status */}
                      {order.status === 'delivered' && (
                        <div style={{ background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', borderRadius: '8px', padding: '10px 14px', fontSize: '0.82rem', fontWeight: '700' }}>
                          ✅ Commande livrée et clôturée. Montant facturé : {Number(order.final_price || order.revenue).toFixed(2)} CHF
                        </div>
                      )}

                      {/* 5. Rejected status */}
                      {order.status === 'rejected' && (
                        <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '8px', padding: '10px 14px', fontSize: '0.82rem', fontWeight: '700' }}>
                          ❌ Commande refusée.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 3: EXPÉDIER (EXACT IDENTICAL WORKFLOW TO PROTOTYPE)    */}
        {/* ========================================================= */}
        {activeTab === 'expedite' && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '18px' }}>
            {/* Expedite Sub-Navigation Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #1e293b',
              borderRadius: '12px',
              padding: '8px 14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => setExpediteSubView('discovery')}
                  style={{
                    background: expediteSubView === 'discovery' ? '#0284c7' : 'none',
                    border: 'none',
                    color: expediteSubView === 'discovery' ? '#fff' : '#94a3b8',
                    borderRadius: '8px',
                    padding: '6px 14px',
                    fontSize: '0.82rem',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  🔍 Tournées disponibles
                </button>
                <button
                  onClick={() => { setExpediteSubView('create'); setExpediteStep('entry'); }}
                  style={{
                    background: expediteSubView === 'create' ? '#0284c7' : 'none',
                    border: 'none',
                    color: expediteSubView === 'create' ? '#fff' : '#94a3b8',
                    borderRadius: '8px',
                    padding: '6px 14px',
                    fontSize: '0.82rem',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  📦 Nouvelle expédition
                </button>
                <button
                  onClick={() => setExpediteSubView('orders')}
                  style={{
                    background: expediteSubView === 'orders' ? '#0284c7' : 'none',
                    border: 'none',
                    color: expediteSubView === 'orders' ? '#fff' : '#94a3b8',
                    borderRadius: '8px',
                    padding: '6px 14px',
                    fontSize: '0.82rem',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  📄 Mes expéditions ({connectedOrders.length})
                </button>
              </div>
            </div>

            {/* Sub-view 1: Discovery Split View */}
            {expediteSubView === 'discovery' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px',
                flex: 1,
                minHeight: '520px'
              }}>
                {/* Left Column: Tournées disponibles */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Filters row */}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input
                        type="text"
                        placeholder="Filtrer par NPA ou ville (ou cliquez sur la carte)..."
                        value={discoveryFilterZip}
                        onChange={e => setDiscoveryFilterZip(e.target.value)}
                        style={{
                          width: '100%',
                          background: '#131e36',
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          color: '#fff',
                          fontSize: '0.82rem'
                        }}
                      />
                      {discoveryFilterZip && (
                        <button
                          onClick={() => setDiscoveryFilterZip('')}
                          style={{ position: 'absolute', right: '8px', top: '8px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* List of Route Cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', maxHeight: '540px' }}>
                    {filteredDiscoveryJobs.map((job, idx) => {
                      const isHovered = hoveredRouteId === job.id || (!hoveredRouteId && idx === 0);
                      const dateBlock = idx % 2 === 0 ? { month: 'AUG', day: '21', time: '07:30' } : { month: 'AUG', day: '28', time: '07:30' };

                      return (
                        <div
                          key={job.id}
                          onMouseEnter={() => setHoveredRouteId(job.id)}
                          onClick={() => { setExpediteSubView('create'); setExpediteStep('entry'); }}
                          style={{
                            background: isHovered ? '#1e293b' : '#131e36',
                            border: isHovered ? '1px solid #38bdf8' : '1px solid #233354',
                            borderRadius: '12px',
                            padding: '14px 18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {/* Left: Date Block */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingRight: '16px',
                            borderRight: '1px solid #334155',
                            minWidth: '60px'
                          }}>
                            <span style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8' }}>{dateBlock.month}</span>
                            <span style={{ fontSize: '1.35rem', fontWeight: '900', color: '#fff', lineHeight: '1.1' }}>{dateBlock.day}</span>
                            <span style={{ fontSize: '0.65rem', color: '#64748b' }}>{dateBlock.time}</span>
                          </div>

                          {/* Center: Carrier & Route */}
                          <div style={{ flex: 1, paddingLeft: '16px' }}>
                            <span style={{
                              background: 'rgba(56, 189, 248, 0.12)',
                              color: '#38bdf8',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              fontSize: '0.68rem',
                              fontWeight: '800',
                              letterSpacing: '0.3px',
                              textTransform: 'uppercase'
                            }}>
                              {orgName}
                            </span>
                            <h4 style={{ margin: '4px 0 0 0', fontSize: '0.98rem', fontWeight: '800', color: '#f8fafc' }}>
                              {job.name}
                            </h4>
                          </div>

                          {/* Right: Price */}
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                            <span style={{ fontSize: '1.25rem', fontWeight: '900', color: '#fff' }}>
                              {Number(job.price_per_stop || 24).toFixed(0)}
                            </span>
                            <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '700' }}>CHF</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right Column: Swiss Map View */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8' }}>
                    Aperçu géographique des tournées ({activeShipperRoute?.name || 'Toutes les tournées'})
                  </div>
                  <ConnectPostalMap
                    selectedNpas={(activeShipperRoute?.postal_codes || []).map(n => n.postal_code || n)}
                    allRoundsNpas={allCoveredPostalCodes}
                    onZipClick={(zip) => setDiscoveryFilterZip(zip)}
                    height="500px"
                  />
                </div>
              </div>
            )}

            {/* Sub-view 2: Full Expedition Workflow (Step 1 entry & Step 2 assign) */}
            {expediteSubView === 'create' && (
              <div style={{ maxWidth: '820px', margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '1.45rem', fontWeight: '800', color: '#fff' }}>
                  Nouvelle expédition
                </h2>

                {/* ------------------------------------------------------------- */}
                {/* STEP 1: ENTRY (Add multi pickups and deliveries)              */}
                {/* ------------------------------------------------------------- */}
                {expediteStep === 'entry' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Empty placeholder if no stops */}
                    {expediteStops.length === 0 && (
                      <div style={{
                        border: '2px dashed #334155',
                        borderRadius: '16px',
                        padding: '40px',
                        textAlign: 'center',
                        color: '#94a3b8',
                        fontSize: '0.95rem',
                        fontWeight: '600'
                      }}>
                        Commencez par ajouter un arrêt
                      </div>
                    )}

                    {/* Render each stop card */}
                    {expediteStops.map((stop, index) => {
                      const isPickup = stop.type === 'PICKUP';
                      const typeIndex = expediteStops.slice(0, index).filter(s => s.type === stop.type).length + 1;
                      const pickupStops = expediteStops.filter(s => s.type === 'PICKUP');

                      return (
                        <div
                          key={stop.id}
                          style={{
                            background: '#131e36',
                            border: `1px solid ${isPickup ? '#1e3a8a' : '#14532d'}`,
                            borderLeft: `5px solid ${isPickup ? '#0284c7' : '#22c55e'}`,
                            borderRadius: '16px',
                            padding: '20px 24px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.25)'
                          }}
                        >
                          {/* Card Top Header */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#fff' }}>
                              {isPickup ? `Ramassage ${typeIndex}` : `Livraison ${typeIndex}`}
                            </h3>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <button
                                type="button"
                                onClick={() => handleFillMyAddress(stop.id)}
                                style={{
                                  background: 'rgba(56, 189, 248, 0.1)',
                                  border: '1px solid rgba(56, 189, 248, 0.3)',
                                  color: '#38bdf8',
                                  borderRadius: '8px',
                                  padding: '5px 10px',
                                  fontSize: '0.78rem',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <User size={13} />
                                <span>Utiliser mon adresse</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleRemoveStop(stop.id)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                              >
                                <Trash size={16} />
                              </button>
                            </div>
                          </div>

                          {/* Client Selector from DB */}
                          {dbClients.length > 0 && (
                            <div style={{ background: '#0f172a', border: '1px solid #1e3a8a', borderRadius: '10px', padding: '10px 14px' }}>
                              <label style={{ fontSize: '0.74rem', color: '#38bdf8', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                <User size={13} color="#38bdf8" />
                                <span>Sélectionner un client de la base (auto-remplissage)</span>
                              </label>
                              <select
                                defaultValue=""
                                onChange={(e) => {
                                  const selectedId = e.target.value;
                                  if (!selectedId) return;
                                  const cl = dbClients.find(c => String(c.id) === String(selectedId));
                                  if (cl) {
                                    handleUpdateStop(stop.id, 'client_name', cl.name || '');
                                    handleUpdateStop(stop.id, 'street', cl.address || cl.street || '');
                                    handleUpdateStop(stop.id, 'zip', cl.postal_code || cl.npa || '');
                                    handleUpdateStop(stop.id, 'city', cl.city || '');
                                    if (cl.description || cl.instructions) {
                                      handleUpdateStop(stop.id, 'description', cl.description || cl.instructions || '');
                                    }
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  background: '#131e36',
                                  border: '1px solid #0284c7',
                                  borderRadius: '8px',
                                  padding: '7px 10px',
                                  color: '#fff',
                                  fontSize: '0.82rem',
                                  cursor: 'pointer'
                                }}
                              >
                                <option value="">-- Choisir un client existant ({dbClients.length} clients) --</option>
                                {dbClients.map(c => (
                                  <option key={c.id} value={c.id}>
                                    {c.name} — {c.city || ''} {c.postal_code ? `(${c.postal_code})` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Inputs Grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1fr', gap: '14px' }}>
                            {/* Nom du client */}
                            <div>
                              <label style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Nom du client</label>
                              <input
                                type="text"
                                placeholder="ex. Boulangerie Dupont"
                                value={stop.client_name}
                                onChange={e => handleUpdateStop(stop.id, 'client_name', e.target.value)}
                                style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem' }}
                              />
                            </div>

                            {/* Poids */}
                            <div>
                              <label style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Poids (kg) *</label>
                              <input
                                type="number"
                                step="0.5"
                                placeholder="1"
                                value={stop.weight}
                                onChange={e => handleUpdateStop(stop.id, 'weight', e.target.value)}
                                style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem' }}
                              />
                            </div>

                            {/* Surface */}
                            <div>
                              <label style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Surface</label>
                              <select
                                value={stop.ground_area}
                                onChange={e => handleUpdateStop(stop.id, 'ground_area', e.target.value)}
                                style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem' }}
                              >
                                {SURFACE_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Address & Description */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                            <div>
                              <label style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Rue et numéro *</label>
                              <input
                                type="text"
                                placeholder="Route de la Gare 3"
                                value={stop.street}
                                onChange={e => handleUpdateStop(stop.id, 'street', e.target.value)}
                                style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem' }}
                              />
                            </div>

                            <div>
                              <label style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Description / Marchandise</label>
                              <input
                                type="text"
                                placeholder="Caisse de lait, palette de légumes..."
                                value={stop.description}
                                onChange={e => handleUpdateStop(stop.id, 'description', e.target.value)}
                                style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem' }}
                              />
                            </div>
                          </div>

                          {/* NPA & Ville */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <div style={{ width: '110px' }}>
                                <label style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '600', display: 'block', marginBottom: '4px' }}>NPA *</label>
                                <input
                                  type="text"
                                  placeholder="1420"
                                  value={stop.zip}
                                  onChange={e => handleUpdateStop(stop.id, 'zip', e.target.value)}
                                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem', fontWeight: '700' }}
                                />
                              </div>

                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Ville *</label>
                                <input
                                  type="text"
                                  placeholder="Lausanne"
                                  value={stop.city}
                                  onChange={e => handleUpdateStop(stop.id, 'city', e.target.value)}
                                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem' }}
                                />
                              </div>
                            </div>

                            {/* Ramassage Associé (Only for Delivery) */}
                            {!isPickup && (
                              <div>
                                <label style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Ramassage associé</label>
                                <select
                                  value={stop.dependency_idx !== null ? String(stop.dependency_idx) : ''}
                                  onChange={e => handleUpdateStop(stop.id, 'dependency_idx', e.target.value !== '' ? parseInt(e.target.value) : null)}
                                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem' }}
                                >
                                  <option value="">— Non associé —</option>
                                  {pickupStops.map((pStop, pIdx) => (
                                    <option key={pStop.id} value={pIdx}>
                                      Ramassage {pIdx + 1} — {pStop.city || pStop.client_name || 'sans ville'}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Add Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', margin: '8px 0' }}>
                      <button
                        type="button"
                        onClick={() => handleAddStop('PICKUP')}
                        style={{
                          background: '#131e36',
                          border: '1px solid #0284c7',
                          color: '#38bdf8',
                          borderRadius: '10px',
                          padding: '10px 20px',
                          fontSize: '0.88rem',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Plus size={16} />
                        <span>Ajouter un Ramassage</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleAddStop('DELIVERY')}
                        style={{
                          background: '#131e36',
                          border: '1px solid #22c55e',
                          color: '#22c55e',
                          borderRadius: '10px',
                          padding: '10px 20px',
                          fontSize: '0.88rem',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Plus size={16} />
                        <span>Ajouter une Livraison</span>
                      </button>
                    </div>

                    {/* Bottom Action Bar */}
                    <div style={{
                      background: '#131e36',
                      border: '1px solid #233354',
                      borderRadius: '16px',
                      padding: '16px 20px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '12px'
                    }}>
                      <button
                        type="button"
                        onClick={() => setExpediteSubView('discovery')}
                        style={{ background: '#1e293b', border: '1px solid #334155', color: '#cbd5e1', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600' }}
                      >
                        Annuler
                      </button>

                      <button
                        type="button"
                        onClick={handleContinueToAssign}
                        style={{
                          background: '#0284c7',
                          border: 'none',
                          color: '#fff',
                          borderRadius: '10px',
                          padding: '10px 24px',
                          fontSize: '0.92rem',
                          fontWeight: '800',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>Continuer</span>
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* ------------------------------------------------------------- */}
                {/* STEP 2: ASSIGN (Select a job for each stop in order)          */}
                {/* ------------------------------------------------------------- */}
                {expediteStep === 'assign' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {expediteStops.map((stop, index) => {
                      const isPickup = stop.type === 'PICKUP';
                      const typeIndex = expediteStops.slice(0, index).filter(s => s.type === stop.type).length + 1;
                      const chosenJob = connectedJobs.find(j => String(j.id) === String(stop.round_id));
                      const isSpecial = chosenJob && !(chosenJob.postal_codes || []).some(n => String(n.postal_code || n).trim() === String(stop.zip).trim());

                      return (
                        <div
                          key={stop.id}
                          style={{
                            background: '#131e36',
                            border: `1px solid ${isPickup ? '#1e3a8a' : '#14532d'}`,
                            borderLeft: `5px solid ${isPickup ? '#0284c7' : '#22c55e'}`,
                            borderRadius: '16px',
                            padding: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}
                        >
                          {/* Label & Location Summary */}
                          <div>
                            <span style={{
                              fontSize: '0.72rem',
                              fontWeight: '800',
                              letterSpacing: '0.5px',
                              textTransform: 'uppercase',
                              color: isPickup ? '#38bdf8' : '#22c55e'
                            }}>
                              {isPickup ? `RAMASSAGE ${typeIndex}` : `LIVRAISON ${typeIndex}`}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: '#cbd5e1', marginTop: '4px' }}>
                              <MapPin size={14} color="#94a3b8" />
                              <span><strong>{stop.client_name}</strong> · {stop.street}, {stop.zip} {stop.city}</span>
                            </div>
                          </div>

                          {/* Selected Round Card (Clickable to change round) */}
                          <div
                            onClick={() => setPickerStopIndex(index)}
                            style={{
                              background: '#0f172a',
                              border: '1px solid #334155',
                              borderRadius: '12px',
                              padding: '14px 18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer'
                            }}
                          >
                            <div>
                              <h4 style={{ margin: '0 0 2px 0', fontSize: '0.98rem', fontWeight: '800', color: '#fff' }}>
                                {orgName}
                              </h4>
                              <div style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                                {chosenJob?.name || 'Fribourg - Vevey - Sierre'} · {formatShortDate(chosenJob?.planned_start || chosenJob?.start_datetime)}
                              </div>
                            </div>

                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '1.2rem', fontWeight: '900', color: '#fff' }}>
                                {Number(chosenJob?.price_per_stop || 24).toFixed(2)}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                CHF {isSpecial ? 'min.' : 'estimé'}
                              </div>
                            </div>
                          </div>

                          {/* Special Request Budget Input */}
                          {isSpecial && (
                            <div style={{
                              background: 'rgba(245, 158, 11, 0.08)',
                              border: '1px solid rgba(245, 158, 11, 0.3)',
                              borderRadius: '10px',
                              padding: '12px 16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <div>
                                <span style={{ fontSize: '0.78rem', color: '#f59e0b', fontWeight: '800' }}>Votre budget max. (demande spéciale)</span>
                                <div style={{ fontSize: '0.72rem', color: '#cbd5e1' }}>NPA {stop.zip} hors zone standard</div>
                              </div>

                              <input
                                type="number"
                                placeholder="ex. 85"
                                value={stop.budget_max}
                                onChange={e => handleUpdateStop(stop.id, 'budget_max', e.target.value)}
                                style={{ width: '120px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '6px 10px', color: '#fff', fontSize: '0.85rem' }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Dark Sticky Total & Confirmation Bar */}
                    <div style={{
                      background: '#0a0f1d',
                      border: '1px solid #1e293b',
                      borderRadius: '16px',
                      padding: '20px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.6)'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px' }}>
                          TOTAL
                        </div>
                        <div style={{ fontSize: '1.65rem', fontWeight: '900', color: '#fff' }}>
                          {totalOrderPrice.toFixed(2)} CHF
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <button
                          type="button"
                          onClick={() => setExpediteStep('entry')}
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <ChevronLeft size={16} />
                          <span>Modifier les arrêts</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setExpediteSubView('discovery')}
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.85rem' }}
                        >
                          Annuler
                        </button>

                        <button
                          type="button"
                          onClick={handleSubmitExpediteOrder}
                          style={{
                            background: '#0284c7',
                            border: 'none',
                            color: '#fff',
                            borderRadius: '10px',
                            padding: '12px 24px',
                            fontSize: '0.92rem',
                            fontWeight: '800',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 16px rgba(2, 132, 199, 0.4)'
                          }}
                        >
                          <span>→ Valider l'expédition</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sub-view 3: My Submitted Orders */}
            {expediteSubView === 'orders' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
                {connectedOrders.map(order => (
                  <div
                    key={order.id}
                    style={{
                      background: '#131e36',
                      border: '1px solid #233354',
                      borderRadius: '14px',
                      padding: '18px 22px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontWeight: '800', color: '#38bdf8' }}>#{order.id}</span>
                        <strong style={{ fontSize: '1rem', color: '#fff' }}>{order.customer_name}</strong>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '1.1rem', fontWeight: '900', color: '#fff' }}>
                          {Number(order.revenue || 48.0).toFixed(2)} CHF
                        </span>
                        <span style={{
                          background: order.status === 'accepted' ? 'rgba(34, 197, 94, 0.15)' : order.status === 'counter_offer' ? 'rgba(245, 158, 11, 0.15)' : order.status === 'delivered' ? 'rgba(56, 189, 248, 0.15)' : order.status === 'rejected' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                          color: order.status === 'accepted' ? '#22c55e' : order.status === 'counter_offer' ? '#f59e0b' : order.status === 'delivered' ? '#38bdf8' : order.status === 'rejected' ? '#ef4444' : '#eab308',
                          borderRadius: '6px',
                          padding: '3px 10px',
                          fontSize: '0.76rem',
                          fontWeight: '800'
                        }}>
                          {order.status === 'accepted' ? 'Confirmée par le transporteur' : order.status === 'counter_offer' ? 'Contre-offre reçue' : order.status === 'delivered' ? 'Livrée' : order.status === 'rejected' ? 'Refusée' : 'En attente'}
                        </span>
                      </div>
                    </div>

                    {/* Counter offer action if received */}
                    {order.status === 'counter_offer' && order.counter_offer_price && (
                      <div style={{
                        background: 'rgba(245, 158, 11, 0.1)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: '10px',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          <strong style={{ color: '#f59e0b', fontSize: '0.88rem' }}>Offre du transporteur : {Number(order.counter_offer_price).toFixed(2)} CHF</strong>
                          <div style={{ fontSize: '0.76rem', color: '#cbd5e1' }}>Le transporteur a proposé ce tarif pour prendre en charge vos étapes.</div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleRespondToOffer(order.id, 'reject')}
                            style={{ background: '#1e293b', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '6px', padding: '6px 12px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}
                          >
                            Refuser
                          </button>
                          <button
                            onClick={() => handleRespondToOffer(order.id, 'accept')}
                            style={{ background: '#22c55e', border: 'none', color: '#0f172a', borderRadius: '6px', padding: '6px 14px', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
                          >
                            Accepter l'offre
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Stops List */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                      {(order.stops || []).map((s, sIdx) => (
                        <div key={sIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#cbd5e1' }}>
                          <span style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: s.type === 'pickup' ? 'rgba(56, 189, 248, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                            color: s.type === 'pickup' ? '#38bdf8' : '#22c55e',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: '800'
                          }}>
                            {s.type === 'pickup' ? 'P' : 'L'}
                          </span>
                          <span><strong>{s.client_name || order.customer_name}</strong> - {s.address} ({s.weight_kg} kg)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------- */}
      {/* 3. MODAL: ROUND PICKER SHEET (Select/Change Round for a stop) */}
      {/* ------------------------------------------------------------- */}
      {pickerStopIndex !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#131e36',
            border: '1px solid #334155',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '520px',
            padding: '20px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            maxHeight: '85vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800', color: '#fff' }}>
                {orgName}
              </h3>
              <button onClick={() => setPickerStopIndex(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Section 1: Demande standard à un transporteur */}
              <div>
                <div style={{ background: 'rgba(56, 189, 248, 0.12)', color: '#38bdf8', padding: '6px 12px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '800', marginBottom: '8px' }}>
                  Demande standard à un transporteur
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {getRoundOptionsForStop(expediteStops[pickerStopIndex]).filter(o => o.isStandard).map(opt => {
                    const isSelected = String(expediteStops[pickerStopIndex]?.round_id) === String(opt.roundId);
                    return (
                      <div
                        key={opt.roundId}
                        onClick={() => {
                          handleUpdateStop(expediteStops[pickerStopIndex].id, 'round_id', String(opt.roundId));
                          setPickerStopIndex(null);
                        }}
                        style={{
                          background: isSelected ? '#1e293b' : '#0f172a',
                          border: isSelected ? '1px solid #38bdf8' : '1px solid #1e293b',
                          borderRadius: '10px',
                          padding: '12px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input type="radio" checked={isSelected} readOnly />
                          <div>
                            <div style={{ fontWeight: '800', color: '#fff', fontSize: '0.88rem' }}>{opt.transporterName}</div>
                            <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{opt.name} · {opt.dateStr}</div>
                          </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: '800', color: '#fff', fontSize: '0.95rem' }}>{opt.price.toFixed(2)}</div>
                          <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>CHF / arrêt</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Section 2: Demande spéciale à un transporteur */}
              <div>
                <div style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', padding: '6px 12px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '800', marginBottom: '8px' }}>
                  Demande spéciale à un transporteur
                  <div style={{ fontSize: '0.68rem', fontWeight: '500', opacity: 0.9 }}>NPA hors zone ou hors limites poids / volume</div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {getRoundOptionsForStop(expediteStops[pickerStopIndex]).filter(o => !o.isStandard).map(opt => {
                    const isSelected = String(expediteStops[pickerStopIndex]?.round_id) === String(opt.roundId);
                    return (
                      <div
                        key={opt.roundId}
                        onClick={() => {
                          handleUpdateStop(expediteStops[pickerStopIndex].id, 'round_id', String(opt.roundId));
                          setPickerStopIndex(null);
                        }}
                        style={{
                          background: isSelected ? '#1e293b' : '#0f172a',
                          border: isSelected ? '1px solid #f59e0b' : '1px solid #1e293b',
                          borderRadius: '10px',
                          padding: '12px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input type="radio" checked={isSelected} readOnly />
                          <div>
                            <div style={{ fontWeight: '800', color: '#fff', fontSize: '0.88rem' }}>{opt.transporterName}</div>
                            <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{opt.name} · {opt.dateStr}</div>
                            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                              <span style={{ background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', borderRadius: '4px', padding: '1px 6px', fontSize: '0.68rem', fontWeight: '800' }}>
                                DEMANDE SPÉCIALE
                              </span>
                              {!opt.servesZip && (
                                <span style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#f87171', borderRadius: '4px', padding: '1px 6px', fontSize: '0.68rem', fontWeight: '800' }}>
                                  NPA non desservi
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: '800', color: '#fff', fontSize: '0.95rem' }}>{opt.price.toFixed(2)}</div>
                          <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>CHF min.</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* 4. MODAL: PUBLIER / DUPLIQUER UNE MISSION                     */}
      {/* ------------------------------------------------------------- */}
      {showNewTourneeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#131e36',
            border: '1px solid #334155',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '640px',
            padding: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '6px', borderRadius: '8px' }}>
                  <Truck size={20} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800', color: '#fff' }}>
                  {duplicatingTournee ? 'Dupliquer les conditions vers une mission' : editingTournee ? 'Modifier la mission mutualisée' : 'Publier une mission'}
                </h3>
              </div>
              <button onClick={() => { setShowNewTourneeModal(false); setDuplicatingTournee(null); setEditingTournee(null); }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveTournee} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* 1. Job Selection */}
              {!editingTournee && (
                <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '10px', padding: '14px' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '800', color: '#38bdf8', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Sparkles size={14} />
                    <span>{duplicatingTournee ? 'Sélectionner le Job Loops planifié cible' : 'Sélectionner un Job planifié (Auto-remplissage)'}</span>
                  </label>
                  <select
                    value={tourneeForm.job_id}
                    onChange={e => handleSelectJobForPublication(e.target.value)}
                    required
                    style={{ width: '100%', background: '#131e36', border: '1px solid #334155', borderRadius: '8px', padding: '10px', color: '#fff', fontSize: '0.85rem' }}
                  >
                    <option value="">-- Choisir une mission planifiée --</option>
                    {plannedJobs.map(job => (
                      <option key={job.id} value={job.id}>
                        #{job.id} - {job.display_name || job.round_name} {job.truck_name ? `(${job.truck_name})` : ''} - {job.status}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* 2. Mission Name */}
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '4px', display: 'block' }}>
                  Nom commercial de la tournée
                </label>
                <input
                  type="text"
                  value={tourneeForm.name}
                  onChange={e => setTourneeForm({ ...tourneeForm, name: e.target.value })}
                  placeholder="ex: Fribourg - Vevey - Sierre"
                  required
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.88rem' }}
                />
              </div>

              {/* 3. Planning statique (Date et créneau horaire) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '4px', display: 'block' }}>
                    Date de la tournée
                  </label>
                  <input
                    type="date"
                    value={tourneeForm.planned_date}
                    onChange={e => setTourneeForm({ ...tourneeForm, planned_date: e.target.value })}
                    required
                    style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.85rem' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '4px', display: 'block' }}>
                    Heure début
                  </label>
                  <input
                    type="time"
                    value={tourneeForm.planned_start_time}
                    onChange={e => setTourneeForm({ ...tourneeForm, planned_start_time: e.target.value })}
                    required
                    style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.85rem' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '4px', display: 'block' }}>
                    Heure fin estimée
                  </label>
                  <input
                    type="time"
                    value={tourneeForm.planned_end_time}
                    onChange={e => setTourneeForm({ ...tourneeForm, planned_end_time: e.target.value })}
                    required
                    style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              {/* 4. Single Base Price per Stop */}
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#38bdf8', marginBottom: '4px', display: 'block' }}>
                  Prix de base par arrêt (CHF)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={tourneeForm.price_per_stop}
                  onChange={e => setTourneeForm({ ...tourneeForm, price_per_stop: e.target.value })}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.85rem' }}
                />
              </div>

              {/* 5. Conditions de Livraison & Limites */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '4px', display: 'block' }}>
                    Poids max / arrêt (kg)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={tourneeForm.max_weight_per_stop}
                    onChange={e => setTourneeForm({ ...tourneeForm, max_weight_per_stop: e.target.value })}
                    style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.85rem' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '4px', display: 'block' }}>
                    Volume max / arrêt
                  </label>
                  <select
                    value={tourneeForm.volume_condition}
                    onChange={e => setTourneeForm({ ...tourneeForm, volume_condition: e.target.value })}
                    style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.85rem' }}
                  >
                    {SURFACE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '4px', display: 'block' }}>
                    Max colis / arrêt
                  </label>
                  <input
                    type="number"
                    value={tourneeForm.max_packages_per_stop}
                    onChange={e => setTourneeForm({ ...tourneeForm, max_packages_per_stop: e.target.value })}
                    style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              {/* 6. Specific Delivery Instructions */}
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '4px', display: 'block' }}>
                  Instructions spécifiques de livraison
                </label>
                <textarea
                  rows={2}
                  value={tourneeForm.special_instructions}
                  onChange={e => setTourneeForm({ ...tourneeForm, special_instructions: e.target.value })}
                  placeholder="ex: Livraisons matinales prioritaires. Hayon recommandé. Température dirigée (Froid positif 2-4°C)."
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.82rem' }}
                />
              </div>

              {/* 7. Auto-accept Option Toggle */}
              <div style={{
                background: '#0f172a',
                border: '1px solid #1e293b',
                borderRadius: '8px',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#fff' }}>
                    Acceptation automatique (Auto-Accept)
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    Valider automatiquement toute commande respectant 100% des conditions et des zones NPA
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={tourneeForm.auto_accept}
                  onChange={e => setTourneeForm({ ...tourneeForm, auto_accept: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
              </div>

              {/* 8. Postal Codes Desservis */}
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#c084fc', marginBottom: '4px', display: 'block' }}>
                  Codes postaux suisses desservis (NPA séparés par des virgules)
                </label>
                <textarea
                  rows={3}
                  value={tourneeForm.postal_codes_input}
                  onChange={e => setTourneeForm({ ...tourneeForm, postal_codes_input: e.target.value })}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '0.82rem', fontFamily: 'monospace' }}
                />
              </div>

              {/* Submit / Cancel Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => { setShowNewTourneeModal(false); setDuplicatingTournee(null); setEditingTournee(null); }}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#cbd5e1', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer' }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{
                    background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
                    border: 'none',
                    color: '#0f172a',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    fontWeight: '800',
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(56, 189, 248, 0.3)'
                  }}
                >
                  {duplicatingTournee ? 'Confirmer la duplication' : editingTournee ? 'Mettre à jour' : 'Publier la mission'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* COST SIMULATION & MARGIN IMPACT MODAL */}
      {simulationOrder && (
        <OrderCostSimulationModal
          order={simulationOrder}
          simulation={simulationData}
          isLoading={isSimulatingCosts}
          onClose={() => {
            setSimulationOrder(null);
            setSimulationData(null);
          }}
          onAcceptAndIntegrate={handleAcceptAndIntegrate}
          onChangeTargetJob={(jobId) => handleSimulateCosts(simulationOrder, jobId)}
        />
      )}
    </div>
  );
}
